import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export const LOCALSTORAGE_KEY = 'toast-log' as const;

interface ToastOptions {
  html: string;
  displayLength: number;
  inDuration: number;
  outDuration: number;
  classes: string;
  completeCallback: (() => void) | null;
  activationPercent: number;
}

export interface ToastAPI {
  show: (html: string, duration?: number, callback?: () => void) => Toast;
  dismissAll: () => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

interface ExtendedHTMLElement extends HTMLElement {
  M_Toast?: Toast;
}

export interface ExtendedWindow extends Window {
  _Toaster?: typeof Toast;
  Toaster?: (html: string, duration?: number, callback?: () => void) => Toast;
  Toast?: ToastAPI;
}

const ToastContext = createContext<ToastAPI | null>(null);

const DEFAULT_OPTIONS: ToastOptions = {
  html: '',
  displayLength: 4000,
  inDuration: 300,
  outDuration: 375,
  classes: '',
  completeCallback: null,
  activationPercent: 0.8,
} as const;

export class Toast {
  public options: ToastOptions;
  public message: string;
  public panning = false;
  public timeRemaining: number;
  public el: HTMLElement;
  public counterInterval?: NodeJS.Timeout;
  public wasSwiped = false;
  public startingXPos = 0;
  public xPos = 0;
  public deltaX = 0;
  public velocityX = 0;
  public time = 0;

  private static _toasts: Toast[] = [];
  private static _container: HTMLElement | null = null;
  private static _draggedToast: Toast | null = null;

  constructor(html: string, displayLength?: number, callback?: () => void) {
    this.options = {
      ...DEFAULT_OPTIONS,
      html,
      displayLength: displayLength ?? DEFAULT_OPTIONS.displayLength,
      completeCallback: callback ?? null,
    };
    this.message = this.options.html;
    this.timeRemaining = this.options.displayLength;

    if (Toast._toasts.length === 0) {
      Toast._createContainer();
    }

    Toast._toasts.push(this);
    const toastElement = this._createToast();
    (toastElement as ExtendedHTMLElement).M_Toast = this;
    this.el = toastElement;
    this._animateIn();
    this._setTimer();

    this._logToStorage();
  }

  private _logToStorage(): void {
    if (!window.localStorage) return;

    const now = new Date();
    const toastLogData = window.localStorage.getItem(LOCALSTORAGE_KEY) || '{}';
    const toastLog = JSON.parse(toastLogData);

    toastLog.messages ??= [];
    toastLog.messages.push({
      timestamp: now,
      message: this.message,
    });
    toastLog.lastUpdated = now;

    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(toastLog));
  }

  static get defaults(): ToastOptions {
    return DEFAULT_OPTIONS;
  }

  static getInstance(el: HTMLElement): Toast {
    return (el as ExtendedHTMLElement).M_Toast!;
  }

  static _createContainer(): void {
    const container = document.createElement('div');
    container.id = 'toast-container';

    const events = [
      ['touchstart', Toast._onDragStart],
      ['touchmove', Toast._onDragMove],
      ['touchend', Toast._onDragEnd],
      ['mousedown', Toast._onDragStart],
    ] as const;

    events.forEach(([event, handler]) => {
      container.addEventListener(event, handler);
    });

    document.addEventListener('mousemove', Toast._onDragMove);
    document.addEventListener('mouseup', Toast._onDragEnd);

    document.body.appendChild(container);
    Toast._container = container;
  }

  static _removeContainer(): void {
    document.removeEventListener('mousemove', Toast._onDragMove);
    document.removeEventListener('mouseup', Toast._onDragEnd);

    Toast._container?.remove();
    Toast._container = null;
  }

  static _onDragStart = (e: Event): void => {
    const toastElement = (e.target as Element)?.closest('.toast') as HTMLElement;
    if (e.target && toastElement) {
      const toast = (toastElement as ExtendedHTMLElement).M_Toast!;
      toast.panning = true;
      Toast._draggedToast = toast;
      toast.el.classList.add('panning');
      toast.el.style.transition = '';
      toast.startingXPos = Toast._xPos(e);
      toast.time = Date.now();
      toast.xPos = Toast._xPos(e);
    }
  };

  static _onDragMove = (e: Event): void => {
    const toast = Toast._draggedToast;
    if (!toast) return;

    e.preventDefault();
    const currentXPos = Toast._xPos(e);
    toast.deltaX = Math.abs(toast.xPos - currentXPos);
    toast.xPos = currentXPos;
    const currentTime = Date.now();
    toast.velocityX = toast.deltaX / (currentTime - toast.time);
    toast.time = currentTime;

    const totalDeltaX = toast.xPos - toast.startingXPos;
    const activationDistance = toast.el.offsetWidth * toast.options.activationPercent;

    Object.assign(toast.el.style, {
      transform: `translateX(${totalDeltaX}px)`,
      opacity: String(1 - Math.abs(totalDeltaX / activationDistance)),
    });
  };

  static _onDragEnd = (): void => {
    const toast = Toast._draggedToast;
    if (!toast) return;

    toast.panning = false;
    toast.el.classList.remove('panning');

    const totalDeltaX = toast.xPos - toast.startingXPos;
    const activationDistance = toast.el.offsetWidth * toast.options.activationPercent;
    const shouldBeDismissed = Math.abs(totalDeltaX) > activationDistance || toast.velocityX > 1;

    if (shouldBeDismissed) {
      toast.wasSwiped = true;
      toast.dismiss();
    } else {
      Object.assign(toast.el.style, {
        transition: 'transform .2s, opacity .2s',
        transform: '',
        opacity: '',
      });
    }
    Toast._draggedToast = null;
  };

  private static _xPos(e: Event): number {
    const touchEvent = e as TouchEvent;
    if (touchEvent.targetTouches?.length >= 1) {
      return touchEvent.targetTouches[0].clientX;
    }
    return (e as MouseEvent).clientX;
  }

  static dismissAll(): void {
    Toast._toasts.forEach((toast) => toast.dismiss());
  }

  private _createToast(): HTMLElement {
    const toast = document.createElement('div');
    toast.classList.add('toast');

    if (this.options.classes) {
      toast.classList.add(...this.options.classes.split(' ').filter(Boolean));
    }

    const isHTMLElement = (obj: unknown): obj is HTMLElement =>
      obj instanceof HTMLElement ||
      (obj && typeof obj === 'object' && obj.nodeType === 1 && typeof obj.nodeName === 'string');

    if (isHTMLElement(this.message)) {
      toast.appendChild(this.message as HTMLElement);
    } else {
      toast.innerHTML = String(this.message);
    }

    Toast._container?.appendChild(toast);
    return toast;
  }

  private _animateIn(): void {
    queueMicrotask(() => {
      this.el.classList.add('animate-in');
    });
  }

  private _setTimer(): void {
    if (this.timeRemaining === 0 || this.timeRemaining === Infinity) {
      return;
    }

    this.counterInterval = setInterval(() => {
      if (!this.panning) {
        this.timeRemaining -= 20;
      }

      if (this.timeRemaining <= 0) {
        this.dismiss();
      }
    }, 20);
  }

  dismiss(): void {
    if (this.counterInterval) {
      clearInterval(this.counterInterval);
    }

    const activationDistance = this.el.offsetWidth * this.options.activationPercent;

    if (this.wasSwiped) {
      Object.assign(this.el.style, {
        transition: 'transform .05s, opacity .05s',
        transform: `translateX(${activationDistance}px)`,
        opacity: '0',
      });
    }

    this.el.classList.add('animate-out');

    setTimeout(() => {
      this.options.completeCallback?.();
      this.el.remove();

      const index = Toast._toasts.indexOf(this);
      if (index > -1) {
        Toast._toasts.splice(index, 1);
      }

      if (Toast._toasts.length === 0) {
        Toast._removeContainer();
      }
    }, this.options.outDuration);
  }
}

export const initToast = () => {
  // Initialize global window properties
  (window as ExtendedWindow)._Toaster = Toast;
  (window as ExtendedWindow).Toaster = (html: string, duration?: number, callback?: () => void) =>
    new Toast(html, duration, callback);

  // Expose Toast API methods directly on window
  (window as ExtendedWindow).Toast = {
    show: (html: string, duration?: number, callback?: () => void) => new Toast(html, duration, callback),
    dismissAll: Toast.dismissAll,
  };

  return Toast;
};

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toastApi, setToastApi] = useState<ToastAPI | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const windowAny = window as ExtendedWindow;

    if (!windowAny.Toaster) {
      const Toast = initToast();
      setToastApi({
        show: (html: string, duration?: number, callback?: () => void) => new Toast(html, duration, callback),
        dismissAll: Toast.dismissAll,
      });
    } else {
      setToastApi({
        show: windowAny.Toaster,
        dismissAll: windowAny._Toaster.dismissAll,
      });
    }
  }, []);

  return <ToastContext.Provider value={toastApi}>{children}</ToastContext.Provider>;
};

export const useToast = (): ToastAPI | null => {
  const context = useContext(ToastContext);

  return typeof window === 'undefined' ? null : context;
};
